import {
  createRoom,
  currentGame,
  decide,
  findBotModel,
  isGameOver,
  replay,
  slotOfColor,
  timestamp,
  toFen,
  describeRoomError,
  type Actor,
  type Command,
  type RoomId,
  type RoomState,
  type TimeControl,
} from '@chess/core'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createEngine, type Engine } from '../engine/uci.js'
import { loadName } from '../lib/storage.js'

export type ConnectionStatus = 'local' | 'connecting' | 'open' | 'closed'

/**
 * What the room screen needs, however the room is actually driven.
 *
 * Hotseat and multiplayer differ only in this object: the same reducer runs in
 * the browser for one and inside the Durable Object for the other, so the whole
 * UI is written once.
 */
export interface RoomConnection {
  readonly state: RoomState
  readonly actor: Actor
  readonly status: ConnectionStatus
  readonly opponentOnline: boolean
  readonly send: (command: Command) => void
  readonly lastError: string | null
  readonly clearError: () => void
  /** Add to `Date.now()` to get the authority's clock. Zero when local. */
  readonly nowOffsetMs: number
  /** False until real state has arrived; the room shows a skeleton until then. */
  readonly ready: boolean
}

/**
 * A clock that advances on its own, for rendering countdowns. Ten times a
 * second is enough for tenths without burning battery.
 */
export const useNow = (active: boolean, intervalMs = 100): number => {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!active) return
    const id = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(id)
  }, [active, intervalMs])

  return now
}

/**
 * Hotseat: both seats are played from one browser, so the actor is always
 * whoever is to move. The room reducer does not know or care.
 */
export const useLocalRoom = (control: TimeControl): RoomConnection => {
  const [state, setState] = useState<RoomState>(() => {
    const now = timestamp(Date.now())
    const fresh = createRoom('local' as RoomId, control, now)
    return [0, 1].reduce((room, slot) => {
      const decided = decide(
        room,
        { type: 'arrive', name: slot === 0 ? 'White' : 'Black' },
        { now, actor: { kind: 'player', slot: slot as 0 | 1 } },
      )
      return decided.ok ? replay(room, decided.value) : room
    }, fresh)
  })

  const [lastError, setLastError] = useState<string | null>(null)
  const stateRef = useRef(state)
  stateRef.current = state

  const send = useCallback((command: Command): void => {
    const current = stateRef.current
    const slot = slotOfColor(currentGame(current).game.position.turn, currentGame(current).index)
    const decided = decide(current, command, {
      now: timestamp(Date.now()),
      actor: { kind: 'player', slot },
    })

    if (!decided.ok) {
      if (command.type !== 'tick') setLastError(describeRoomError(decided.error))
      return
    }
    if (decided.value.length > 0) setState(replay(current, decided.value))
  }, [])

  // Nothing ticks on its own, so something must ask whether a flag has fallen.
  useEffect(() => {
    const id = window.setInterval(() => send({ type: 'tick' }), 200)
    return () => window.clearInterval(id)
  }, [send])

  const slot = slotOfColor(currentGame(state).game.position.turn, currentGame(state).index)

  return {
    state,
    actor: { kind: 'player', slot },
    status: 'local',
    opponentOnline: true,
    send,
    lastError,
    clearError: useCallback(() => setLastError(null), []),
    nowOffsetMs: 0,
    ready: true,
  }
}

/** The human's seat in an engine room. The engine holds seat 1, like any bot. */
const HUMAN_SLOT = 0 as const
const ENGINE_SLOT = 1 as const

export interface EngineRoomConnection extends RoomConnection {
  /** The engine could not be loaded or died; the page should say so plainly. */
  readonly engineFailed: boolean
}

/**
 * Against the engine: the same local reducer as hotseat, but the seats are
 * real. The human is seat 0 and every command they issue is theirs, so the
 * board keeps their orientation instead of flipping each ply; Stockfish drives
 * seat 1 from a Web Worker the way the Durable Object drives an LLM bot.
 *
 * Entirely local by design — it works offline, and none of it is archived.
 */
export const useEngineRoom = (control: TimeControl, modelId: string): EngineRoomConnection => {
  const [state, setState] = useState<RoomState>(() => {
    const now = timestamp(Date.now())
    const fresh = createRoom('engine' as RoomId, control, now, { kind: 'ai', model: modelId })
    const decided = decide(
      fresh,
      { type: 'arrive', name: loadName() || 'You' },
      { now, actor: { kind: 'player', slot: HUMAN_SLOT } },
    )
    return decided.ok ? replay(fresh, decided.value) : fresh
  })

  const [lastError, setLastError] = useState<string | null>(null)
  const [engineFailed, setEngineFailed] = useState(false)
  const stateRef = useRef(state)
  stateRef.current = state
  const engineRef = useRef<Engine | null>(null)
  const thinkingRef = useRef(false)

  const dispatch = useCallback((command: Command, slot: 0 | 1): void => {
    const current = stateRef.current
    const decided = decide(current, command, {
      now: timestamp(Date.now()),
      actor: { kind: 'player', slot },
    })
    if (!decided.ok) {
      if (command.type !== 'tick' && slot === HUMAN_SLOT) {
        setLastError(describeRoomError(decided.error))
      }
      return
    }
    if (decided.value.length > 0) setState(replay(current, decided.value))
  }, [])

  const send = useCallback(
    (command: Command): void => dispatch(command, HUMAN_SLOT),
    [dispatch],
  )

  // One engine per mounted room. StrictMode mounts effects twice in dev, so
  // creation lives here with a disposer, not in render.
  useEffect(() => {
    let cancelled = false
    const model = findBotModel(modelId)
    const settings = model?.engine ?? { elo: null, movetimeMs: 300 }

    void createEngine(settings)
      .then((engine) => {
        if (cancelled) engine.dispose()
        else engineRef.current = engine
      })
      .catch(() => {
        if (!cancelled) setEngineFailed(true)
      })

    return () => {
      cancelled = true
      engineRef.current?.dispose()
      engineRef.current = null
    }
  }, [modelId])

  // Flag-fall detection, same as hotseat.
  useEffect(() => {
    const id = window.setInterval(() => dispatch({ type: 'tick' }, HUMAN_SLOT), 200)
    return () => window.clearInterval(id)
  }, [dispatch])

  // The engine's half of the conversation: move when it is its turn, and take
  // a rematch when offered. There is no draw handling because the side panel
  // hides the draw button in a bot game — a button that only ever leads to a
  // refusal is worse than no button, and unreachable code is worse still.
  useEffect(() => {
    const room = state
    const slot = currentGame(room)

    // The human offered a rematch; the engine has no reason to hesitate.
    if (isGameOver(slot.game.status) && room.rematchOfferedBy === HUMAN_SLOT) {
      dispatch({ type: 'offerRematch' }, ENGINE_SLOT)
      return
    }

    if (isGameOver(slot.game.status)) return

    if (slotOfColor(slot.game.position.turn, slot.index) !== ENGINE_SLOT) return
    if (thinkingRef.current) return

    const engine = engineRef.current
    if (engine === null) return

    thinkingRef.current = true
    // If the world changes while the engine thinks — resignation, rematch —
    // the stale answer must not be played into the new position.
    const forGame = slot.index
    const forPly = slot.game.history.length

    void engine
      .bestMove(toFen(slot.game.position))
      .then((uci) => {
        const nowCurrent = currentGame(stateRef.current)
        if (nowCurrent.index !== forGame || nowCurrent.game.history.length !== forPly) return
        if (isGameOver(nowCurrent.game.status)) return
        dispatch({ type: 'move', uci }, ENGINE_SLOT)
      })
      .catch(() => setEngineFailed(true))
      .finally(() => {
        thinkingRef.current = false
      })
  }, [state, dispatch])

  return {
    state,
    actor: { kind: 'player', slot: HUMAN_SLOT },
    status: 'local',
    opponentOnline: true,
    send,
    lastError,
    clearError: useCallback(() => setLastError(null), []),
    nowOffsetMs: 0,
    ready: true,
    engineFailed,
  }
}
